import {
  renderDonutForPdf,
  renderBarForPdf,
  renderTypeGroupedBarForPdf,
  drawChartRow,
  drawChartSection,
} from "../pdfCharts";
import { BAR_PALETTE, DONUT_COLORS } from "../../../../utils/chartConfig";
import { formatWhole } from "../../../../utils/numberFormat";
import {
  getWasteDisposalTotals,
  buildWasteBarChartData,
  buildWasteBarChartDataPeligrosos,
  DISPOSAL_LABEL_KEYS,
} from "./TableGraf";
import {
  buildWasteComparisonData,
  buildWasteComparisonDataPeligrosos,
} from "./wasteChartData";

// Los 3 gráficos de TableGraf.js (dona "Métodos de disposición" + barra
// anual No peligrosos + barra anual Peligrosos) como SVG vectorial — misma
// lógica de datos que ya usa TableGraf.js en pantalla, reutilizada tal cual.
export async function renderWasteTableGrafForPdf(totales, totalesMethods, filters, t) {
  const donutData = getWasteDisposalTotals(totalesMethods, filters?.year).map((d) => ({
    ...d,
    label: t(DISPOSAL_LABEL_KEYS[d.type] || d.type),
  }));
  const [donut, barNoPeligrosos, barPeligrosos] = await Promise.all([
    renderDonutForPdf(donutData, DONUT_COLORS.waste, { showPercentLabels: true }),
    // Regla 1 (AJUSTES DE MAGNITUDES NUMÉRICAS): espacio fino separador de
    // miles, sin decimales — mismo criterio que ya usa el tooltip de estos
    // gráficos en pantalla (TableGraf.js).
    renderBarForPdf(buildWasteBarChartData(totales), "Kg", BAR_PALETTE, { yFormatter: formatWhole }),
    renderBarForPdf(buildWasteBarChartDataPeligrosos(totales), "Kg", BAR_PALETTE, { yFormatter: formatWhole }),
  ]);
  return { donut, barNoPeligrosos, barPeligrosos };
}

// "Residuos no peligrosos"/"Residuos peligrosos" por tipo — barras
// agrupadas por año dentro de cada categoría de residuo, misma config que
// WasteComparisonChart/WasteComparisonChartPeligrosos, siempre con los 3
// años completos (sin el toggle de la UI).
export async function renderWasteComparisonForPdf(resultsByYear, valueBaseline, t) {
  const { barData } = buildWasteComparisonData(resultsByYear, valueBaseline, t);
  return renderTypeGroupedBarForPdf(barData, null, BAR_PALETTE, { yFormatter: formatWhole });
}

export async function renderWasteComparisonPeligrososForPdf(resultsByYear, valueBaseline, t) {
  const { barData } = buildWasteComparisonDataPeligrosos(resultsByYear, valueBaseline, t);
  return renderTypeGroupedBarForPdf(barData, null, BAR_PALETTE, { yFormatter: formatWhole });
}

// Orquesta el dibujado de los 3 gráficos de TableGraf.js + las 2 secciones
// de comparación por tipo en el PDF: se pasa como `renderCustomCharts` a
// KPIExportIconButton. Mismo criterio de layout ya resuelto en
// Energía/Combustibles/Agua/Efluentes: fila de 3 tarjetas con altura fija,
// después las secciones anchas de a 2 (acá solo hay 2), con techo de altura
// para no ocupar toda la hoja si van juntas.
export async function drawWasteCustomCharts(doc, ctx, { totales, totalesMethods, filters, valueBaseline, t }) {
  const { margin, pageWidth, pageHeight, imgWidth } = ctx;
  let yOffset = ctx.yOffset;

  const { donut, barNoPeligrosos, barPeligrosos } = await renderWasteTableGrafForPdf(totales, totalesMethods, filters, t);
  const cards = [
    { title: t("non_hazardous_waste"), ...barNoPeligrosos },
    { title: t("hazardous_waste"), ...barPeligrosos },
    { title: t("disposalMethods"), ...donut },
  ];
  const CHART_ROW_H = 210;
  if (CHART_ROW_H > pageHeight - margin - yOffset) {
    doc.addPage();
    yOffset = margin;
  }
  await drawChartRow(doc, cards, { x: margin, y: yOffset, availW: imgWidth, cardH: CHART_ROW_H, emptyMessage: t("no_data_available") });
  yOffset += CHART_ROW_H + 10;
  donut.cleanup?.();
  barNoPeligrosos.cleanup?.();
  barPeligrosos.cleanup?.();

  const GAP = 10;
  const MIN_EACH_H = 260;
  const MAX_EACH_H = 340;
  const sections = [
    { title: t("composition_non_hazardous_waste_generation"), render: () => renderWasteComparisonForPdf(totales, valueBaseline, t) },
    { title: t("composition_hazardous_waste_generation"), render: () => renderWasteComparisonPeligrososForPdf(totales, valueBaseline, t) },
  ];

  let availForGroup = pageHeight - margin - yOffset - GAP;
  if (availForGroup < MIN_EACH_H * sections.length) {
    doc.addPage();
    yOffset = margin;
    availForGroup = pageHeight - margin - yOffset - GAP;
  }
  const sectionH = Math.min(Math.floor(availForGroup / sections.length), MAX_EACH_H);

  for (const section of sections) {
    const result = await section.render();
    await drawChartSection(doc, { x: margin, y: yOffset, width: imgWidth, height: sectionH, title: section.title, emptyMessage: t("no_data_available"), ...result });
    yOffset += sectionH + GAP;
    result.cleanup?.();
  }

  return yOffset;
}
